import React, { useEffect, useState } from 'react';
import { X, Mic, Film, FileText, Mail, Megaphone } from 'lucide-react';
import { TEMPLATES, WorkflowTemplate } from '../../utils/marketingTemplates';
import './NewContentModal.css';

interface NewContentModalProps {
  onSubmit: (input: {
    title: string;
    template: WorkflowTemplate;
    recordingDate?: Date | null;
    guestName?: string | null;
  }) => void | Promise<void>;
  onClose: () => void;
}

interface TemplateOption {
  id?: string;
  templateId?: string;
  label: string;
  icon: React.ReactNode;
  enabled: boolean;
  description: string;
}

const TEMPLATE_OPTIONS: TemplateOption[] = [
  {
    templateId: 'podcast-video-v1',
    label: 'Podcast Video',
    icon: <Mic size={18} />,
    enabled: true,
    description: 'Shoot · Upload · Edit · Publish · Notify · Archive',
  },
  {
    label: 'Reel / Short',
    icon: <Film size={18} />,
    enabled: false,
    description: 'Vertical short — coming soon',
  },
  {
    label: 'Blog Post',
    icon: <FileText size={18} />,
    enabled: false,
    description: 'Article workflow — coming soon',
  },
  {
    label: 'Email Blast',
    icon: <Mail size={18} />,
    enabled: false,
    description: 'Newsletter — coming soon',
  },
  {
    label: 'Ad Campaign',
    icon: <Megaphone size={18} />,
    enabled: false,
    description: 'Paid creative — coming soon',
  },
];

const NewContentModal: React.FC<NewContentModalProps> = ({ onSubmit, onClose }) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('podcast-video-v1');
  const [title, setTitle] = useState('');
  const [recordingDate, setRecordingDate] = useState('');
  const [guestName, setGuestName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const template = TEMPLATES[selectedTemplateId];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || submitting || !template) return;
    setSubmitting(true);
    try {
      await onSubmit({
        title: title.trim(),
        template,
        recordingDate: recordingDate ? new Date(recordingDate + 'T00:00:00') : null,
        guestName: guestName.trim() || null,
      });
    } catch (err) {
      console.error('Error creating marketing content:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mh-modal-overlay" onClick={onClose}>
      <div className="mh-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="New content">
        <div className="mh-modal-header">
          <h3>New Content</h3>
          <button
            className="mh-modal-close"
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mh-modal-body">
            <div className="mh-modal-section">
              <label className="mh-modal-label">Template</label>
              <div className="mh-template-grid">
                {TEMPLATE_OPTIONS.map(opt => {
                  const isSelected = opt.enabled && opt.templateId === selectedTemplateId;
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      className={`mh-template-card ${isSelected ? 'selected' : ''} ${!opt.enabled ? 'disabled' : ''}`}
                      onClick={() => opt.enabled && opt.templateId && setSelectedTemplateId(opt.templateId)}
                      disabled={!opt.enabled}
                    >
                      <span className="mh-template-card-icon">{opt.icon}</span>
                      <span className="mh-template-card-label">{opt.label}</span>
                      <span className="mh-template-card-desc">{opt.description}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mh-modal-section">
              <label className="mh-modal-label" htmlFor="mh-modal-title">
                Title
              </label>
              <input
                id="mh-modal-title"
                className="mh-modal-input mh-modal-input-title"
                placeholder="e.g. Episode 42 – Building Better Doors"
                value={title}
                onChange={e => setTitle(e.target.value)}
                autoFocus
              />
            </div>

            <div className="mh-modal-grid">
              <div className="mh-modal-section">
                <label className="mh-modal-label" htmlFor="mh-modal-recording">
                  Recording date
                </label>
                <input
                  id="mh-modal-recording"
                  type="date"
                  className="mh-modal-input"
                  value={recordingDate}
                  onChange={e => setRecordingDate(e.target.value)}
                />
              </div>

              <div className="mh-modal-section">
                <label className="mh-modal-label" htmlFor="mh-modal-guest">
                  Guest (optional)
                </label>
                <input
                  id="mh-modal-guest"
                  className="mh-modal-input"
                  placeholder="e.g. Jane Smith"
                  value={guestName}
                  onChange={e => setGuestName(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="mh-modal-footer">
            <button type="button" className="pm-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="pm-btn pm-btn-primary"
              disabled={!title.trim() || submitting}
            >
              {submitting ? 'Creating…' : 'Create Content'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewContentModal;
